import { useEffect, useRef, useState } from 'react';
import { useWorkspace } from '../workspace/useWorkspace.ts';
import { computeDisruption, formatPxPerSec } from '../domain/disruption.ts';
import { hasButtonActions } from '../domain/draft.ts';
import { JigState, PatternKind } from '../protocol/constants.ts';
import { PathEditor } from '../features/path-editor/PathEditor.tsx';
import { Recorder } from '../features/recorder/Recorder.tsx';
import { Clicker } from '../features/clicker/Clicker.tsx';
import { StepTable } from '../features/pattern-editor/StepTable.tsx';
import { ProfileLibrary } from '../features/profiles/ProfileLibrary.tsx';
import { PatternPreview } from './PatternPreview.tsx';
import { PATTERN_LABELS, PatternSettings, RunSettings } from './PatternSettings.tsx';
import type { JigStep } from '../protocol/types.ts';

type Tool = 'pattern' | 'path' | 'record' | 'clicker';
const TOOLS: [Tool, string][] = [['pattern', 'Pattern'], ['path', 'Path'], ['record', 'Record'], ['clicker', 'Auto-click']];

export function remainingTime(ms: number): string {
  const seconds = Math.max(0, Math.ceil(ms / 1000));
  if (seconds >= 3600) return `${Math.floor(seconds / 3600)} h ${Math.floor(seconds % 3600 / 60)} min`;
  if (seconds >= 60) return `${Math.floor(seconds / 60)} min ${seconds % 60} s`;
  return `${seconds} s`;
}

function DeviceMark() {
  return <svg viewBox="0 0 64 88" aria-hidden="true" className="production__device-mark">
    <path d="M23 24V9h18v15M23 14h18M28 9v5m8-5v5M20 24h24v47a7 7 0 0 1-7 7H27a7 7 0 0 1-7-7V24Z" />
    <path d="M28 32h8m-8 5h8" /><circle cx="32" cy="62" r="1.5" />
  </svg>;
}

export function ProductionApp() {
  const w = useWorkspace();
  const [tool, setTool] = useState<Tool>('pattern');
  const [profilesOpen, setProfilesOpen] = useState(false);
  // Local input modes survive typing, but not adoption of another draft.
  const [settingsRevision, setSettingsRevision] = useState(0);
  const [command, setCommand] = useState(false);
  const [commandError, setCommandError] = useState<string | null>(null);
  const commandLock = useRef(false);
  const commandSession = useRef(0);
  const { hid, draft } = w;
  const applying = w.pushState.kind === 'busy';
  const ready = w.connected && !w.loading && !w.loadError && w.deviceDraft !== null;
  const state = hid.status?.state;
  const stopping = state === JigState.RUNNING || state === JigState.BOOT_WAIT || state === undefined;
  const clicks = hasButtonActions(draft);
  const disruption = computeDisruption(draft);
  const band = clicks ? 'red' : disruption.band;
  const stateLabel = state === JigState.RUNNING ? 'Running' : state === JigState.BOOT_WAIT ? 'Starting soon'
    : state === JigState.DONE ? 'Finished' : state === JigState.STOPPED ? 'Parked' : 'Checking status…';
  const runLabel = state === JigState.BOOT_WAIT ? 'Stay parked' : stopping ? 'Stop' : state === JigState.DONE ? 'Start again' : 'Start';

  useEffect(() => {
    commandSession.current++;
    commandLock.current = false;
    setCommand(false);
    setCommandError(null);
  }, [hid.transport, w.connected]);

  async function runCommand(action: () => Promise<unknown>) {
    if (commandLock.current) return;
    const session = commandSession.current;
    commandLock.current = true;
    setCommand(true);
    setCommandError(null);
    try { await action(); }
    catch (error) {
      if (session === commandSession.current) setCommandError(error instanceof Error ? error.message : String(error));
    } finally {
      if (session === commandSession.current) { commandLock.current = false; setCommand(false); }
    }
  }

  function useMacro(steps: JigStep[], durationMs?: number) {
    w.adoptMacro(steps, durationMs);
    setSettingsRevision((revision) => revision + 1);
    setTool('pattern');
  }

  const unsupported = hid.phase === 'unsupported';
  const insecure = hid.phase === 'insecure';
  const connecting = hid.phase === 'connecting';
  const failed = hid.phase === 'error';

  return <main className="production">
    <header className="production__header">
      <a className="production__brand" href="./" aria-label="ViMouseJiggler home">
        <span className="production__brand-mark" aria-hidden="true">v<span>m</span></span>
        <span>ViMouseJiggler</span>
      </a>
      <div className="production__header-actions">
        {ready && <button className="production__quiet" type="button" aria-expanded={profilesOpen}
          aria-controls="production-profiles" disabled={applying} onClick={() => setProfilesOpen(!profilesOpen)}>Profiles</button>}
        <span className={`production__connection ${w.connected ? 'production__connection--on' : ''}`}>
          <span className="production__dot" />{w.connected ? 'Connected' : connecting ? 'Connecting' : 'Not connected'}
        </span>
        {w.connected && <button className="production__quiet" type="button" disabled={applying || command}
          onClick={() => void runCommand(hid.disconnect)}>Disconnect</button>}
      </div>
    </header>

    {!w.connected ? <section className="production__welcome" aria-label="Connect device">
      <DeviceMark />
      <h1>{unsupported ? 'Open in Chrome or Edge' : insecure ? 'Secure connection required'
        : failed ? 'Connection interrupted' : connecting ? 'Connecting…' : 'Connect your device'}</h1>
      <p>{unsupported ? 'A desktop browser with device access is required.'
        : insecure ? 'Open this page over HTTPS.'
          : failed ? hid.message : connecting ? 'Please wait.' : 'Plug in your ViMouseJiggler.'}</p>
      {!unsupported && !insecure && <button type="button" className="btn btn--primary" disabled={connecting || command}
        onClick={() => void runCommand(hid.connect)}>{connecting ? 'Connecting…' : failed ? 'Try again' : 'Connect device'}</button>}
      {!failed && hid.message && !unsupported && !insecure && <p role="status">{hid.message}</p>}
      {!unsupported && !insecure && <span className="production__welcome-note">Chrome or Edge · Permission is saved for this site</span>}
    </section> : <>
      <section className={`production__device-bar production__device-bar--${state ?? 'unknown'}`} aria-label="Device status">
        <div className="production__run-state" role="status">
          <span className="production__dot" />
          <strong>{stateLabel}</strong>
          {state === JigState.RUNNING && hid.status && <span>{hid.status.totalMs === 0 ? 'Until stopped' : `${remainingTime(hid.status.totalMs - hid.status.elapsedMs)} left`}</span>}
          {state !== JigState.RUNNING && hid.config && <span>{PATTERN_LABELS[hid.config.kind]} on device</span>}
        </div>
        <button type="button" className={`btn ${stopping ? 'production__stop' : 'btn--primary'}`} disabled={command || (!stopping && (!ready || applying || tool === 'record'))}
          onClick={() => void runCommand(async () => {
            const status = await (stopping ? hid.stopJiggle() : hid.startJiggle());
            if (!status) throw new Error('Could not confirm device state. Try again.');
          })}>{command ? 'Please wait…' : runLabel}</button>
      </section>

      {!ready ? <section className="production__loading" aria-live="polite">
        {w.loadError ? <><h1>Could not load your pattern</h1><p className="bad">{w.loadError}</p>
          <button className="btn" type="button" onClick={() => void w.retryLoad()}>Retry</button></>
          : <p>Loading your pattern…</p>}
      </section> : <>
        <section id="production-profiles" hidden={!profilesOpen} className="production__library">
          <fieldset disabled={applying}>
            <ProfileLibrary compact draft={draft} deviceDraft={w.deviceDraft} currentName={w.profileName}
              onNameChange={w.setProfileName} onLoad={(draft, name) => {
                w.onLoadProfile(draft, name);
                setSettingsRevision((revision) => revision + 1);
                setTool('pattern');
                setProfilesOpen(false);
              }} />
          </fieldset>
        </section>
        <div className="production__workspace-heading">
          <div><span className="production__eyebrow">Workspace</span><h1>{w.profileName || 'Your pattern'}</h1></div>
          <nav className="production__tools" aria-label="Pattern tools">
            {TOOLS.map(([key, label]) => <button type="button" key={key} aria-pressed={tool === key}
              disabled={applying} onClick={() => setTool(key)}>{label}</button>)}
          </nav>
        </div>
        <fieldset className={`production__workspace${tool === 'clicker' ? ' production__workspace--wide' : ''}`} disabled={applying}>
          <div className="production__canvas">
            {tool === 'pattern' && <>
              <PatternPreview draft={draft} />
              <PatternSettings key={settingsRevision} draft={draft} onChange={w.setDraft} />
              {draft.kind === PatternKind.MACRO && <details className="production__steps">
                <summary>Edit steps <span>{draft.steps.length}</span></summary>
                <StepTable compact draft={draft} onChange={w.setDraft} showErrors={w.showErrors} />
              </details>}
            </>}
            {/* Geometry is local authoring state: don't unmount it on adoption/tab changes. */}
            <div hidden={tool !== 'path'}><PathEditor compact onUse={useMacro} /></div>
            {tool === 'record' && <Recorder compact deviceStopped={w.deviceStopped} onEnsureStopped={w.ensureStopped} onUse={useMacro} />}
            {tool === 'clicker' && <Clicker compact onUse={useMacro} />}
          </div>
          {tool !== 'clicker' && <aside className="production__sidebar"><RunSettings key={settingsRevision} draft={draft} onChange={w.setDraft} /></aside>}
        </fieldset>
        {tool === 'pattern' && <section className="production__apply" aria-label="Apply pattern">
          <div className="production__apply-top">
            <div className="production__draft-status" aria-live="polite">
              <strong>{applying ? 'Applying…' : w.dirty ? 'Changes not applied' : hid.config?.saved ? 'On device · saved' : 'On device · not saved'}</strong>
              <span className={`production__impact production__impact--${band}`}>
                {clicks ? 'Clicks & drags' : band === 'green' ? 'Gentle' : band === 'amber' ? 'Noticeable' : 'High movement'}
                {' · '}{disruption.estimated ? '~' : ''}{formatPxPerSec(disruption.pxPerSec)}
              </span>
            </div>
            <div className="production__apply-actions">
              <label className="check"><input type="checkbox" checked={w.saveToFlash} disabled={applying}
                onChange={(event) => w.setSaveToFlash(event.target.checked)} /><span>Keep on device</span></label>
              <button type="button" className="production__quiet" disabled={!w.dirty || applying} onClick={() => {
                w.onResetToDevice();
                setSettingsRevision((revision) => revision + 1);
              }}>Revert</button>
              <button type="button" className="btn btn--primary" disabled={applying || command} onClick={() => void w.onPush()}>
                {applying ? 'Applying…' : 'Apply'}</button>
            </div>
          </div>
          {clicks && <p className="warn production__safety">Saved clicks run on every power-up. Stop here or unplug.</p>}
          {!clicks && band === 'red' && <p className="warn production__safety">High movement may interfere with pointer control.</p>}
          {w.pushState.kind === 'ok' && !w.dirty && <p className="ok" role="status">{w.pushState.saved ? 'Applied and saved.' : 'Applied for this session.'}</p>}
          {w.pushState.kind === 'bad' && <p className="bad" role="alert">{w.pushState.message}</p>}
          {w.showErrors && w.problems.length > 0 && <ul className="problems" role="alert">
            {w.problems.map((problem, i) => <li key={`${problem.field}-${i}`}>{problem.message}</li>)}
          </ul>}
        </section>}
      </>}
    </>}
    {commandError && <p className="bad production__command-error" role="alert">{commandError}</p>}
    <footer className="production__footer"><span>ViMouseJiggler</span><a href="./debug/">Debug</a></footer>
  </main>;
}
